import * as Sentry                                 from '@sentry/angular';
import {HttpErrorResponse}                         from '@angular/common/http';
import {Component, ElementRef, inject, OnInit, ViewChild}                 from '@angular/core';
import {FormBuilder, FormGroup, Validators}        from '@angular/forms';
import {MatStepper}                                from '@angular/material/stepper';
import {ActivatedRoute, Router}                    from '@angular/router';
import {environment}                               from '@src/environments/environment';
import {SignupOptions, SignupPlan, SignupProOffer} from '@src/app/core/models/signup-options.model';
import {AuthService}                               from '../../services/auth.service';
import {CustomValidators}                          from '../../validators/check-password';
import {PaymentService}                            from '@src/app/core/services/payment.service';
import {switchMap}                                 from 'rxjs';

@Component({
    standalone: false,
    selector: 'app-sign-up',
    templateUrl: './sign-up.component.html',
    styleUrls: ['./sign-up.component.scss']
})
export class SignUpComponent implements OnInit
{
    private readonly payments = inject(PaymentService);
    private pricingPreference = inject(ActivatedRoute).snapshot.queryParamMap.get('plan');
    private readonly element: ElementRef<HTMLElement> = inject(ElementRef);
    @ViewChild(MatStepper) private stepper?: MatStepper;
    public unmappedErrors: string[] = [];
    private readonly attemptStorageKey = 'signup-attempt';
    public recoveringAttempt = false;
    private readonly fieldLabels: Record<string, string> = {
        company_name: 'Company name', name: 'Full name', email: 'Email',
        password: 'Password', password_confirmation: 'Confirm password',
    };
    public signupForm: FormGroup;
    public error = '';
    public submitted = false;
    public loading = false;
    public showPassword = false;
    public showPasswordConfirmation = false;
    public readonly passwordRules = [
        {label: '8+ characters', matches: (value: string) => Array.from(value).length >= 8},
        {label: 'Uppercase', matches: (value: string) => /\p{Lu}/u.test(value)},
        {label: 'Lowercase', matches: (value: string) => /\p{Ll}/u.test(value)},
        {label: 'Number', matches: (value: string) => /\p{N}/u.test(value)},
        {label: 'Symbol', matches: (value: string) => /[\p{Z}\p{S}\p{P}]/u.test(value)},
    ].filter((_, index) => environment.environmentName !== 'local' || index === 0);
    public readonly passwordHelpText = environment.environmentName !== 'local'
        ? 'At least 8 characters, with uppercase, lowercase, a number and a symbol. Avoid known compromised passwords. Compromised passwords are checked when you submit.'
        : 'At least 8 characters.';
    public options: SignupOptions | null = null;
    public proPlan: SignupProOffer['plan'] = 'pro_monthly';
    public optionsLoading = false;
    public optionsError = '';
    public planPreferenceMessage = '';

    public constructor(
        private router: Router,
        private fb: FormBuilder,
        private authService: AuthService
    )
    {
    }

    public get f()
    {
        return this.signupForm.controls;
    }

    public fieldError(field: string): string
    {
        const control = this.f[field];
        if (!control || (!this.submitted && !control.touched)) {
            return '';
        }
        const errors = control.errors ?? {};
        if (errors.server) { return errors.server; }
        if (errors.required) { return `${this.fieldLabels[field]} is required.`; }
        if (errors.email) { return 'Enter a valid email address.'; }
        if (errors.minlength) { return 'Use at least 8 characters.'; }
        if (errors.passwordRules) { return 'Meet the password requirements below.'; }
        if (errors.not_matching) { return "Passwords don't match."; }
        return '';
    }

    private focusErrors(): void
    {
        const field = Object.keys(this.fieldLabels).find(name => this.f[name].invalid);
        if (field && this.stepper) {
            this.stepper.selectedIndex = 0;
        }
        const selector = field ? `input[formControlName="${field}"]` : '#signup-error-summary';
        setTimeout(() => this.element.nativeElement.querySelector<HTMLElement>(selector)?.focus());
    }

    public ngOnInit(): void
    {
        const attempt = this.restoreAttempt();
        this.recoveringAttempt = !!attempt;
        this.signupForm = this.fb.group({
            company_name: [null, [Validators.required]],
            name: [null, [Validators.required]],
            email: [null, [Validators.required, Validators.email]],
            password: [null, [Validators.required, control => {
                const value = control.value ?? '';
                if (!value) {
                    return null;
                }
                const length = Array.from(value).length;
                if (length < 8) {
                    return {minlength: {requiredLength: 8, actualLength: length}};
                }
                return this.passwordRules.every(rule => rule.matches(value)) ? null : {passwordRules: true};
            }]],
            password_confirmation: [null, [Validators.required]],
            plan: [attempt?.plan ?? null],
            registration_key: [attempt?.key ?? crypto.randomUUID()],
            frontend_url: [`${environment.localPath}/email-verified`],
        }, {
            validators: CustomValidators.MatchingPasswords
        });
        this.loadSignupOptions();
    }

    private restoreAttempt(): {key: string; plan: SignupPlan} | null
    {
        try {
            const attempt = JSON.parse(sessionStorage.getItem(this.attemptStorageKey) ?? 'null');
            if (attempt && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(attempt.key) && ['payg', 'pro_monthly', 'pro_annual'].includes(attempt.plan)) {
                return {key: attempt.key, plan: attempt.plan};
            }
        } catch {
            // Signup still works when browser storage is unavailable.
        }
        return null;
    }

    private saveAttempt(): void
    {
        try {
            if (!this.restoreAttempt()) {
                sessionStorage.setItem(this.attemptStorageKey, JSON.stringify({key: this.f.registration_key.value, plan: this.f.plan.value}));
            }
        } catch {
            // The form's in-memory key still protects retries without a reload.
        }
    }

    private clearAttempt(): void
    {
        this.recoveringAttempt = false;
        try {
            sessionStorage.removeItem(this.attemptStorageKey);
        } catch {
            // Storage may be blocked by the browser.
        }
    }

    public loadSignupOptions(): void
    {
        this.optionsLoading = true;
        this.optionsError = '';
        this.options = null;
        this.authService.signupOptions().subscribe({
            next: options => {
                this.options = options;
                const plan = this.pricingPreference;
                this.pricingPreference = null;
                // An interrupted registration keeps its original plan for safe replay.
                if (plan && !this.recoveringAttempt) {
                    if ((plan === 'payg' || plan === 'pro_monthly' || plan === 'pro_annual') && this.planAvailable(plan)) {
                        this.f.plan.setValue(plan);
                    } else {
                        this.planPreferenceMessage = 'That plan is unavailable. Please choose a plan below.';
                    }
                }
                if (this.proSelected) {
                    this.proPlan = this.f.plan.value;
                }
                this.optionsLoading = false;
            },
            error: (error: HttpErrorResponse) => {
                Sentry.captureException(error, {tags: {signup_stage: 'load_plans'}});
                this.optionsLoading = false;
                this.optionsError = 'Signup is temporarily unavailable. Please try again shortly.';
            }
        });
    }

    public planAvailable(plan: SignupPlan): boolean
    {
        if (!this.options?.plans.includes(plan)) {
            return false;
        }
        return plan === 'payg' || (this.options.pro_offers?.some(offer => offer.plan === plan) ?? false);
    }

    public get proSelected(): boolean
    {
        return this.f.plan.value === 'pro_monthly' || this.f.plan.value === 'pro_annual';
    }

    public get proOffer(): SignupProOffer | undefined
    {
        return this.options?.pro_offers?.find(offer => offer.plan === this.proPlan);
    }

    public switchProPrice(plan: SignupProOffer['plan']): void
    {
        this.proPlan = plan;
        if (this.proSelected) {
            this.f.plan.setValue(plan);
        }
    }

    public selectPro(): void
    {
        this.f.plan.setValue(this.proPlan);
    }

    public get selectedPlanAvailable(): boolean
    {
        return this.planAvailable(this.f.plan.value);
    }

    public continueToPlan(stepper: MatStepper): void
    {
        this.submitted = true;
        this.signupForm.markAllAsTouched();
        if (this.signupForm.valid) {
            this.error = '';
            stepper.next();
        } else {
            this.focusErrors();
        }
    }

    public onSubmit(): void
    {
        if (this.loading || this.optionsLoading || this.optionsError) {
            return;
        }
        this.submitted = true;
        this.signupForm.markAllAsTouched();
        if (this.signupForm.invalid) {
            this.focusErrors();
            return;
        }
        if (!this.selectedPlanAvailable) {
            return;
        }
        this.error = '';
        this.unmappedErrors = [];

        const registration = {...this.signupForm.value};
        // Dormant signup uses the existing registration contract without plan fields.
        if (!this.options.plan_signup_enabled) {
            delete registration.plan;
            if (!this.options.registration_replay_enabled) {
                delete registration.registration_key;
            }
        }

        if (registration.registration_key) { this.saveAttempt(); }
        this.loading = true;
        this.signupForm.disable();

        this.authService.register(registration).subscribe({
            next: response => {
                try {
                    this.authService.authenticateUser(response);
                } catch (error) {
                    Sentry.captureException(error);
                    this.authService.deauthenticateUser();
                    this.signupForm.enable();
                    this.loading = false;
                    this.error = 'Your account was created, but we could not sign you in. Please sign in to continue.';
                    this.focusErrors();
                    return;
                }

                this.clearAttempt();
                if (response.onboarding?.checkout_required === true) {
                    this.startSignupCheckout(response.onboarding.selected_plan ?? registration.plan);
                } else {
                    this.router.navigate(['/app/sites']);
                }
            },
            error: (response: HttpErrorResponse) => {
                this.signupForm.enable();
                this.loading = false;

                if (response.status === 409 && response.error?.code === 'signup_plan_unavailable') {
                    this.clearAttempt();
                    this.error = 'The available plans have changed. Choose an available plan and submit again. Your details have been kept.';
                    this.loadSignupOptions();
                    this.focusErrors();
                    return;
                }

                if (response.status === 409 || response.status === 403) {
                    this.error = 'This signup cannot be resumed with these details. Return to login to sign in or reset your password.';
                } else if (response.status === 422) {
                    const errors = response.error?.errors ?? {};
                    for (const [field, messages] of Object.entries(errors)) {
                        const text = Array.isArray(messages) ? messages.filter(message => typeof message === 'string').join(' ') : '';
                        if (!text) { continue; }
                        if (Object.keys(this.fieldLabels).includes(field)) {
                            this.f[field].setErrors({...this.f[field].errors, server: text});
                        } else {
                            this.unmappedErrors.push(text);
                        }
                    }
                    this.error = this.signupForm.invalid || this.unmappedErrors.length
                        ? '' : 'We could not validate your details. Please try again shortly.';
                } else if (response.status === 0 || response.status >= 500) {
                    this.error = registration.registration_key
                        ? 'We could not confirm your signup. Try again with the same details, or return to login if you already have an account.'
                        : 'We could not confirm your signup. Return to login and try signing in before submitting again.';
                } else if (response.status === 429) {
                    this.error = 'Too many signup attempts. Please wait a few minutes before trying again. Your details have been kept.';
                } else {
                    this.error = 'Signup could not be completed. Your details have been kept. Please try again or contact support.';
                }
                this.focusErrors();
            }
        });
    }

    private startSignupCheckout(plan: SignupPlan): void
    {
        const returnUrl = `${environment.localPath}/app/settings/7`;

        this.payments.getCatalogue().pipe(switchMap(catalogue => {
            const interval = plan === 'pro_annual' ? 'year' : 'month';
            const offer = catalogue.offers.find(candidate => candidate.kind === 'base' && candidate.plan === 'pro' && candidate.interval === interval);

            if (!offer) {
                throw new Error('The selected signup price is unavailable.');
            }

            return this.payments.checkout({
                price_id: offer.price_id,
                success_url: `${returnUrl}?session_id={CHECKOUT_SESSION_ID}`, cancel_url: `${returnUrl}?payment_cancelled=true`
            });
        })).subscribe({
            next: checkout => {
                if (checkout?.url) {
                    window.location.href = checkout.url;
                } else {
                    this.router.navigate(['/app/settings/7']);
                }
            },
            error: error => {
                Sentry.captureException(error, {tags: {signup_stage: 'checkout'}});
                this.router.navigate(['/app/settings/7']);
            },
        });
    }
}
